import { useState } from "react";
import { linkApp, type LinkAppRequest, type LinkAppResult } from "../../controllers/apiClient";
import type { Principal } from "../../controllers/oidc";

const BLANK: LinkAppRequest = {
  namespace: "sp-apps", name: "", image: "", port: 8080, replicas: 1,
  db: false, minio: false, openai: false, oauthProxy: false,
  displayName: "", icon: "", home: "", purchaseUrl: "",
};

// Control-plane view: an admin links a sibling app. The BFF generates GitOps manifests, commits
// them to the infra repo (ArgoCD deploys), and registers the app — no kubectl.
export function AdminLink({
  getToken, onLinked,
}: {
  principal: Principal; getToken: () => string | null; onLinked: () => Promise<void>;
}) {
  const [req, setReq] = useState<LinkAppRequest>(BLANK);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<LinkAppResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof LinkAppRequest>(k: K, v: LinkAppRequest[K]) =>
    setReq((r) => ({ ...r, [k]: v }));

  async function submit() {
    setBusy(true); setError(null); setResult(null);
    try {
      const home = req.home || `${req.name}/home`;
      setResult(await linkApp(getToken, { ...req, home, displayName: req.displayName || req.name }));
      await onLinked();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const valid = req.namespace && req.name && req.image;

  return (
    <div className="detail">
      <div className="detail-name"><h1>앱 링크</h1></div>
      <p className="meta">
        새 sibling 백엔드를 GitOps로 연결합니다. compath가 매니페스트를 생성→인프라 저장소에 커밋하면
        ArgoCD가 배포합니다. (kubectl 없음)
      </p>

      <div className="detail-body" style={{ marginTop: 20 }}>
        <div className="detail-main">
          <Card title="배포 스펙">
            <Field label="네임스페이스"><input className="sdui-input" value={req.namespace} onChange={(e) => set("namespace", e.target.value)} /></Field>
            <Field label="앱 이름 (name)"><input className="sdui-input" placeholder="orders" value={req.name} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label="컨테이너 이미지"><input className="sdui-input" placeholder="gitea.compath.dev/sp/orders:v1" value={req.image} onChange={(e) => set("image", e.target.value)} /></Field>
            <div style={{ display: "flex", gap: 14 }}>
              <Field label="포트"><input className="sdui-input" type="number" value={req.port} onChange={(e) => set("port", Number(e.target.value))} /></Field>
              <Field label="레플리카"><input className="sdui-input" type="number" value={req.replicas} onChange={(e) => set("replicas", Number(e.target.value))} /></Field>
            </div>
          </Card>

          <Card title="리소스 · 사이드카">
            <Toggle label="PostgreSQL (CNPG)" on={!!req.db} onChange={(v) => set("db", v)} />
            <Toggle label="MinIO 버킷" on={!!req.minio} onChange={(v) => set("minio", v)} />
            <Toggle label="OpenAI 키" on={!!req.openai} onChange={(v) => set("openai", v)} />
            <Toggle label="oauth2-proxy" on={!!req.oauthProxy} onChange={(v) => set("oauthProxy", v)} />
          </Card>

          <Card title="런처 표시">
            <Field label="표시 이름"><input className="sdui-input" placeholder="주문" value={req.displayName} onChange={(e) => set("displayName", e.target.value)} /></Field>
            <div style={{ display: "flex", gap: 14 }}>
              <Field label="아이콘"><input className="sdui-input" placeholder="cart" value={req.icon} onChange={(e) => set("icon", e.target.value)} /></Field>
              <Field label="홈 화면"><input className="sdui-input" placeholder="orders/edit" value={req.home} onChange={(e) => set("home", e.target.value)} /></Field>
            </div>
            <Field label="구매 URL (유료 앱, 선택)"><input className="sdui-input" placeholder="무료 앱이면 비워두세요" value={req.purchaseUrl} onChange={(e) => set("purchaseUrl", e.target.value)} /></Field>
          </Card>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button className="btn ghost" style={{ width: "auto" }} onClick={() => setReq(BLANK)}>초기화</button>
            <button className="btn primary" style={{ width: "auto" }} disabled={!valid || busy} onClick={submit}>
              {busy ? "링크 중…" : "앱 링크하기 →"}
            </button>
          </div>
          {error && <div className="note" style={{ marginTop: 12, background: "#fdeef0" }}><b style={{ color: "var(--amber)" }}>실패:</b> {error}</div>}
        </div>

        <aside className="aside">
          <div className="info-card">
            <h4>{result ? "링크 완료" : "미리보기"}</h4>
            {!result && <p style={{ fontSize: 14, color: "var(--ink3)" }}>제출하면 생성된 매니페스트와 커밋 결과가 여기에 표시됩니다.</p>}
            {result && (
              <>
                <div className="kv"><span>앱</span><span className="mono">{result.app.id}</span></div>
                <div className="kv"><span>네임스페이스</span><span className="mono">{result.namespace}</span></div>
                <div className="kv"><span>커밋</span><span className="mono">{result.commit.slice(0, 12)}</span></div>
                <div style={{ marginTop: 10, fontSize: 13, color: "var(--ink3)" }}>생성 파일 · {result.files.length}</div>
                {result.files.map((f) => <div key={f} className="mono" style={{ fontSize: 13, color: "var(--ink2)", padding: "3px 0" }}>+ {f}</div>)}
              </>
            )}
          </div>
          {result?.nextSteps?.length ? (
            <div className="note">
              <b>다음 단계</b><br />
              {result.nextSteps.map((s, i) => <div key={i}>· {s}</div>)}
            </div>
          ) : (
            <div className="note"><b>커밋 → ArgoCD가 자동 배포합니다.</b><br />compath는 kubectl을 실행하지 않습니다.</div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="info-card" style={{ marginBottom: 16 }}>
      <h4 style={{ fontSize: 16, marginBottom: 14 }}>{title}</h4>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="sdui-field" style={{ flex: 1 }}>
      <span className="sdui-label" style={{ fontSize: 14 }}>{label}</span>
      {children}
    </label>
  );
}

function Toggle({ label, on, onChange }: { label: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
      <span style={{ fontSize: 16 }}>{label}</span>
      <button
        type="button"
        onClick={() => onChange(!on)}
        style={{
          width: 44, height: 26, borderRadius: 999, border: "none", position: "relative",
          background: on ? "var(--blue)" : "#d1d6db", transition: "background .15s",
        }}
      >
        <span style={{ position: "absolute", top: 3, left: on ? 21 : 3, width: 20, height: 20,
          borderRadius: "50%", background: "#fff", transition: "left .15s" }} />
      </button>
    </label>
  );
}
